<script setup lang="ts">
import {computed} from 'vue';

const props = defineProps<{
  name?: string;
  exampleHtml: string;
}>();

// Directus authors examples in two shapes: rich HTML (e.g. journals use nested
// <ul>/<li> lists) and plain text with bare "\n" line separators (e.g.
// conceptualization worksheets). Plain text needs `white-space: pre-line` so the
// newlines render as line breaks instead of collapsing into one blob (IPD-2466);
// but applying it to real markup turns every newline between tags into an extra
// blank line, so lists/paragraphs render with excessive spacing (IPD-2765).
// Detect markup and only keep `pre-line` for the plain-text case.
const isPlainText = computed(() => !/<[a-z][\s\S]*>/iu.test(props.exampleHtml));

</script>
<template>
  <div class="example">
    <p class="example-title">
      {{ $t('example') }} <span v-if="name">{{ name }}</span>
    </p>
    <div v-safe-html="exampleHtml" class="example-body" :class="{'plain-text': isPlainText}"/>
  </div>
</template>
<style lang="scss" scoped>
.example {
  padding: var(--Spacing-spacing-4);
  @include font-body;
  @include q-card-20;
  > .example-body {
    @include font-body;

    // Only plain-text examples need newline preservation; HTML examples
    // (lists/paragraphs) rely on their own markup for layout — see the
    // isPlainText note in the script block.
    &.plain-text {
      white-space: pre-line;
    }
  }
}

.example-title {
  @include font-body-bold;
  margin-bottom: var(--Spacing-spacing-2) !important;
}
</style>
